<template>
    <div data-component="OrderDetailsTabContent"
         class="order-details-tab-content">
        <OrderTicketSummaryCard v-if="isTicketTab"
                                :tab="tab"
                                :current-order="currentOrder"
                                @select-group="scrollToGroup" />

        <!-- Membership summary box is out of scope for now. -->
        <!--        <OrderMembershipSummaryCard v-if="isMembershipTab"-->
        <!--                                    :tab="tab"-->
        <!--                                    :current-order="currentOrder"-->
        <!--                                    @select-membership="scrollToMembership" />-->

        <OrderDetailsGroup v-for="group in tab.groups"
                           :key="group.ID"
                           :ref="(el) => setGroupRef(group.ID, el)"
                           :group="group"
                           :current-order="currentOrder"
                           :current-branch-name="currentBranch.name"
                           :is-expanded="isGroupExpanded(group)"
                           :open-quickly="pendingScroll?.group.ID === group.ID"
                           @toggle-group="toggleGroup"
                           @expanded="groupExpanded" />
    </div>
</template>

<script>
import ItemType from '@/lib/Items/ItemType';
import OrderDetailsGroup from '@/epos/vue/pages/order-details/components/OrderDetailsGroup';
// import OrderMembershipSummaryCard from '@/epos/vue/pages/order-details/components/OrderMembershipSummaryCard';
import OrderTicketSummaryCard from '@/epos/vue/pages/order-details/components/OrderTicketSummaryCard';
import { mapState } from 'vuex';

/**
 * @typedef {import('@/lib/Orders/orderItemGroups').OrderItemTab} OrderItemTab
 */

// See utilities.less. Applied to whatever has just been scrolled to, then taken back off.
const SCROLL_HIGHLIGHT_CLASS = 'scroll-highlight';
const SCROLL_HIGHLIGHT_MS = 1000;

export default {
    name: 'OrderDetailsTabContent',

    components: {
        OrderDetailsGroup,
        // OrderMembershipSummaryCard,
        OrderTicketSummaryCard,
    },

    props: {
        tab: {
            type: Object,
            required: true,
        },
        currentOrder: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            // Which groups are expanded, grouped by tab.
            expandedGroupIDsByTab: {},

            // OrderDetailsGroup component instances, keyed by group ID, so summary-card
            // clicks can scroll to the right one. Not reactive state - just bookkeeping.
            groupRefs: {},

            /**
             * If a row was clicking in a summary card and is waiting for a group to
             * animate open before scrolling to it.
             *
             * @type {?{group: OrderItemGroup, membership: ?Membership}}
             */
            pendingScroll: null,
        };
    },

    computed: {
        isTicketTab() {
            return this.tab.itemType === ItemType.TICKET;
        },

        isMembershipTab() {
            return this.tab.itemType === ItemType.MEMBERSHIP_PLAN;
        },

        expandedGroupIDs() {
            return this.expandedGroupIDsByTab[this.tab.itemType] || [];
        },

        ...mapState([
            'currentBranch',
        ]),
    },

    watch: {
        /**
         * The first time is accessed by default only the first group expanded.
         * On subsequent visits to a tab which groups are expanded or collapsed are stored in expandedGroupIDsByTab.
         *
         * @param {OrderItemTab} tab
         */
        tab: {
            handler(tab) {
                if (!tab || this.expandedGroupIDsByTab.hasOwnProperty(tab.itemType)) {
                    return;
                }

                const firstGroup = tab.groups?.[0];
                this.expandedGroupIDsByTab = {
                    ...this.expandedGroupIDsByTab,
                    [tab.itemType]: firstGroup ? [firstGroup.ID] : [],
                };
            },
            immediate: true,
        },
    },

    methods: {
        isGroupExpanded(group) {
            return this.expandedGroupIDs.includes(group.ID);
        },

        toggleGroup(group) {
            const updated = this.isGroupExpanded(group)
                ? this.expandedGroupIDs.filter((id) => id !== group.ID)
                : [...this.expandedGroupIDs, group.ID];

            this.expandedGroupIDsByTab = {
                ...this.expandedGroupIDsByTab,
                [this.tab.itemType]: updated,
            };
        },

        setGroupRef(groupID, componentInstance) {
            if (componentInstance) {
                this.groupRefs[groupID] = componentInstance;
            } else {
                delete this.groupRefs[groupID];
            }
        },

        /**
         * Expand a group if it isn't already (never collapses it).
         *
         * @param {OrderItemGroup} group
         */
        expandGroup(group) {
            if (this.isGroupExpanded(group)) {
                return;
            }

            this.expandedGroupIDsByTab = {
                ...this.expandedGroupIDsByTab,
                [this.tab.itemType]: [...this.expandedGroupIDs, group.ID],
            };
        },

        /**
         * @param {OrderItemGroup} group
         */
        scrollToGroup(group) {
            this.expandThenScrollTo(group);
        },

        /**
         * @param {{group: OrderItemGroup, membership: Membership}} row
         */
        scrollToMembership(row) {
            this.expandThenScrollTo(row.group, row.membership);
        },

        /**
         * Expand the group if it isn't already, then scroll to it (or to a membership inside it).
         * The scroll waits for the group to finish opening, because a smooth scroll aims at where
         * the target is when the scroll starts. If it opened after scrolling we would have scrolled to the wrong
         * place if the page grows longer by opening the group.
         *
         * @param {OrderItemGroup} group
         * @param {Membership} [membership]
         */
        expandThenScrollTo(group, membership = null) {
            if (this.isGroupExpanded(group)) {
                this.$nextTick(() => this.scrollIntoView(group, membership));

                return;
            }

            this.pendingScroll = { group, membership };
            this.expandGroup(group);
        },

        /**
         * A group has finished opening. If a summary-card click was waiting on it, this is the
         * point where the target's final position is known and it can be scrolled to.
         *
         * @param {OrderItemGroup} group
         */
        groupExpanded(group) {
            if (this.pendingScroll?.group.ID !== group.ID) {
                return;
            }

            const { membership } = this.pendingScroll;
            this.pendingScroll = null;
            this.scrollIntoView(group, membership);
        },

        /**
         * @param {OrderItemGroup} group
         * @param {Membership} [membership] Scroll to this membership within the group.
         */
        scrollIntoView(group, membership = null) {
            const element = membership
                ? document.querySelector(`[data-membership-id="${membership.getID()}"]`)
                : this.groupRefs[group.ID]?.$el;

            if (!element) {
                return;
            }

            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            this.highlightBriefly(element);
        },

        /**
         * Flash an outline around what was scrolled to, the same way the cart does when it scrolls
         * to a line on the sell screen.
         *
         * @param {HTMLElement} element
         */
        highlightBriefly(element) {
            element.classList.add(SCROLL_HIGHLIGHT_CLASS);

            setTimeout(() => element.classList.remove(SCROLL_HIGHLIGHT_CLASS), SCROLL_HIGHLIGHT_MS);
        },
    },
};
</script>

<style lang="less">
@import "../../../../../less/variables";

@media (min-width: @non-mobile-min) {
    .order-details-tab-content {
        // Space after tabs on desktop only (on mobile the tabs become a bar).
        padding-top: @gutter-width;
    }
}
</style>
